/**
 * InvoicesTable — recent Stripe invoices with PDF + hosted-portal
 * deep links.
 *
 * V1 reference: ``ando-frontend/app/components/billing/InvoicesTable.tsx``.
 * V2 ``BillingSettings`` previously rendered a flat ``<ul>`` of one
 * line per invoice with the link "Apri" only — no PDF download, no
 * date columns, no amount alignment. This component restores V1
 * parity: a 5-column table (Numero / Periodo / Stato / Importo /
 * Azioni) with the latest 10 entries.
 *
 * The component is purely presentational — fetch + loading + error
 * states live in the host (``BillingSettings.tsx``) so a future "show
 * 50 / show all" pagination drops in without touching the row markup.
 */

import type { InvoiceItem } from "@ando/core";
import { buttonVariants, cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Download, ExternalLink } from "lucide-react";

// Reuse the Ando ``Button`` ghost+sm classes verbatim so the inline
// links match the rest of the surface. ``Button asChild`` would have
// been the obvious choice but the primitive ignores the ``asChild``
// prop today (it spreads onto a plain ``<button>``), and ``<a>`` is
// the correct semantic for a download / external link anyway.
const linkButtonClass = buttonVariants({ variant: "ghost", size: "sm" });
import type * as React from "react";

const STATUS_LABEL: Record<string, ReturnType<typeof msg>> = {
  paid: msg`Pagata`,
  open: msg`Aperta`,
  void: msg`Annullata`,
  uncollectible: msg`Non riscuotibile`,
  draft: msg`Bozza`,
};

const STATUS_TONE: Record<string, string> = {
  paid: "bg-success/15 text-success",
  open: "bg-warning/15 text-warning",
  void: "bg-ink-3/10 text-ink-3",
  uncollectible: "bg-error/15 text-error",
  draft: "bg-ink-3/10 text-ink-3",
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatPeriod(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start && !end) return "—";
  if (!end) return formatDate(start);
  if (!start) return formatDate(end);
  return `${formatDate(start)} → ${formatDate(end)}`;
}

function formatAmount(amount: number, currency: string): string {
  const symbol =
    currency.toUpperCase() === "EUR"
      ? "€"
      : currency.toUpperCase() === "USD"
        ? "$"
        : `${currency.toUpperCase()} `;
  return `${symbol}${amount.toFixed(2)}`;
}

export interface InvoicesTableProps {
  readonly invoices: readonly InvoiceItem[];
  readonly className?: string;
}

export const InvoicesTable: React.FC<InvoicesTableProps> = ({
  invoices,
  className,
}) => {
  const { t, i18n } = useLingui();
  // V1 cap at 10 entries — Stripe ordering is "most recent first" so
  // we just slice. The "see all" expansion lives in the Stripe portal.
  const rows = invoices.slice(0, 10);
  if (rows.length === 0) {
    return (
      <p className={cn("text-[12px] text-ink-3", className)}>
        <Trans>Nessuna fattura emessa.</Trans>
      </p>
    );
  }
  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-hairline bg-card/55",
        className,
      )}
    >
      <table className="w-full text-[12px]">
        <thead className="border-hairline border-b bg-ink-1/[0.02] text-left">
          <tr className="text-[11px] text-ink-3 uppercase tracking-wide">
            <th scope="col" className="px-3 py-2 font-medium">
              <Trans>Numero</Trans>
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              <Trans>Periodo</Trans>
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              <Trans>Stato</Trans>
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              <Trans>Importo</Trans>
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              <Trans>Azioni</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((inv) => {
            const tone = STATUS_TONE[inv.status] ?? "bg-ink-3/10 text-ink-3";
            const statusDesc = STATUS_LABEL[inv.status];
            const label = statusDesc ? i18n._(statusDesc) : inv.status;
            const invoiceRef = inv.number ?? inv.id.slice(0, 12);
            return (
              <tr
                key={inv.id}
                className="border-hairline border-t first:border-t-0"
              >
                <td className="px-3 py-2 font-medium text-ink-1">
                  {inv.number ?? inv.id.slice(0, 12)}
                </td>
                <td className="px-3 py-2 text-ink-2">
                  {formatPeriod(inv.period_start, inv.period_end)}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-1.5 py-0.5 font-medium text-[10.5px]",
                      tone,
                    )}
                  >
                    {label}
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-ink-1 tabular-nums">
                  {formatAmount(inv.amount_paid || inv.amount_due, inv.currency)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    {inv.invoice_pdf ? (
                      <a
                        href={inv.invoice_pdf}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={t`Scarica PDF fattura ${invoiceRef}`}
                        className={linkButtonClass}
                      >
                        <Download
                          className="h-3 w-3"
                          strokeWidth={2}
                          aria-hidden
                        />
                        PDF
                      </a>
                    ) : null}
                    {inv.hosted_invoice_url ? (
                      <a
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={t`Apri fattura ${invoiceRef} su Stripe`}
                        className={linkButtonClass}
                      >
                        <ExternalLink
                          className="h-3 w-3"
                          strokeWidth={2}
                          aria-hidden
                        />
                        <Trans>Apri</Trans>
                      </a>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
InvoicesTable.displayName = "InvoicesTable";
